// SPDX-License-Identifier: SEE LICENSE
//
// Canonical metadata for the 13 attestation pillars (the Distributed
// Provenance Manifold). Drives the dashboard grid tiles, the per-pillar
// live panels, and the "under the hood" model cards. Every model
// description is sourced from the on-device code / innovation dossier.

import type { StepKind } from './types';

export interface PillarMeta {
  kind: StepKind;
  index: number;
  name: string;
  code: string; // short scientific code, e.g. CBAHI
  tagline: string;
  accent: string; // hex used for charts/accents
  runsOn: 'on-device' | 'on-device + server';
  /** One-line model architecture summary for the tile. */
  model: string;
  /** Detailed model card lines for the "under the hood" drawer. */
  modelCard: string[];
  /** Cryptographic binding note. */
  crypto: string;
  /** Key telemetry metric keys the device emits during the run. */
  liveMetrics: string[];
}

export const PILLARS: PillarMeta[] = [
  {
    kind: 'antenna',
    index: 1,
    name: 'Antenna Health',
    code: 'CBAHI',
    tagline: 'Cross-band RF reception profiling',
    accent: '#7fe8c9',
    runsOn: 'on-device + server',
    model: 'Leave-One-Out Studentised Residual cross-band test on a Bayesian NIG posterior',
    modelCard: [
      'Concurrent 90s capture of WiFi 2.4/5/6 GHz, LTE RSRP/RSSNR, 5G NR, GPS L1/L5 C/N0, BT-LE, NFC.',
      'Detection: per-band concern z-scores → Leave-One-Out Studentised Residual (LOO-t) under Student-t, df = m−2.',
      'Family-wise control: Bonferroni-corrected min p · m < η (η=0.01).',
      'Baseline: conjugate Normal-Inverse-Gamma posterior over N=52 healthy Note-10 runs (Student-t predictive).',
      'Server recompute via recomputeAnalysis() against the active per-model baseline.',
    ],
    crypto: 'Report canonical JSON → SHA-256 challenge → fresh TEE EC P-256 key, detached ECDSA signature.',
    liveMetrics: ['wifi_2_4', 'wifi_5', 'wifi_6', 'lte_rsrp', 'nr_rsrp', 'gps_l1', 'bt_le'],
  },
  {
    kind: 'battery_soh',
    index: 2,
    name: 'Battery State-of-Health',
    code: 'BDF-SOH',
    tagline: 'Charging-curve image transfer learning',
    accent: '#f0a35e',
    runsOn: 'on-device',
    model: 'GASF/GADF/MTF image encoding + PIACS-RDA logistic-on-logit estimator',
    modelCard: [
      '10-min charging-curve capture (voltage, current, temp) from BatteryManager.',
      'Image encoding: Gramian Angular Summation/Difference Fields + Markov Transition Field → 224×224×3.',
      'Production estimator: Physics-Informed Aging-Curve Synthesis with Real-Device Anchoring (PIACS-RDA).',
      'SoH(%) = 100/(1+exp(−(β0 + Σβi·zi))) over 4 z-standardised features (CC→CV position, dV/dt, taper half-life, ΔT).',
      'Inverse-variance fusion with the load-response SoH (Pillar 3) when both exist.',
    ],
    crypto: 'aliasForCombinedRun / aliasForSohRun TEE key; signed report uploaded via SohUploader.',
    liveMetrics: ['voltage_mv', 'current_ma', 'temp_c', 'soh_pct', 'cc_cv'],
  },
  {
    kind: 'battery_forecast',
    index: 3,
    name: 'Battery Lifespan Forecast',
    code: 'BDDF-LRA',
    tagline: 'Physics-informed death-date forecast',
    accent: '#f0a35e',
    runsOn: 'on-device',
    model: 'Bayesian PIAP + 1-D Kalman + Gaussian Process, inverse-variance fused',
    modelCard: [
      'Three-member ensemble: Bayesian Physics-Informed Analytic Posterior (Nascimento 2023), 2-state Kalman, GP tail (RBF).',
      'Projects SoH → calendar days until DEATH_THRESHOLD_PCT = 80%, 90% band ±1.6449·σ_days.',
      'Coefficient-hash bound: COEF_HASH SHA-256 asserted; backend rejects non-allowlisted coefficient sets.',
      'Honest "preliminary" verdict when < 3 runs or zero signal.',
    ],
    crypto: 'EnsembleCoefficients.COEF_HASH + MODEL_VERSION_HASH bound into the run signature.',
    liveMetrics: ['soh_pct', 'months_to_eol', 'fade_rate'],
  },
  {
    kind: 'touch',
    index: 4,
    name: 'Touch Controller Health',
    code: 'BTHI',
    tagline: 'Behavioural touch health inference',
    accent: '#7fe8c9',
    runsOn: 'on-device + server',
    model: 'Stacking ensemble {RF + HGB + MLP} → Logistic Regression meta',
    modelCard: [
      'Guided 2-min interaction: tap targets, moving target, drag path, multi-touch, idle listen.',
      '58 numeric features per run across a 128-region grid + quadrant sums.',
      'Per-metric Gaussian falloff 100·exp(−z²/2); geometric mean per region (one bad metric drags down).',
      'Regions with insufficient data → null, not zero (honesty surface).',
    ],
    crypto: 'Canonical BTHI report signed with fresh TEE key; uploaded to /touch/runs.',
    liveMetrics: ['tap_reaction_ms', 'jitter_px', 'multitouch_sep_px', 'idle_events'],
  },
  {
    kind: 'oled',
    index: 5,
    name: 'Display Burn-In',
    code: 'SRDT',
    tagline: 'Self-referential display tomography',
    accent: '#c9a8f0',
    runsOn: 'on-device + server',
    model: 'Bilinear-detrended ratio + AR(1)-VIF-corrected z-score baseline (v5)',
    modelCard: [
      'Front-camera captures test patterns reflected in a user-held mirror; 4-point homography (pure-Kotlin DLT).',
      '8×8 = 64 region sampling, sRGB→linear→CIE XYZ, per-region luminance Y + chromaticity.',
      'v5 scoring: panel-median ratio → bilinear spatial detrend → z-score vs baseline; max|z| + 64-vector.',
      'Stretched-exponential lifetime: L(t)/L0 = exp(−(t/τ)^β), τ=50000h, β=0.6.',
    ],
    crypto: 'aliasForOledRun TEE key per run; oled-baseline.json + .sha256 bound.',
    liveMetrics: ['max_z', 'uniformity_db', 'color_shift_de2000', 'burn_in_regions'],
  },
  {
    kind: 'vibro',
    index: 6,
    name: 'Structural Integrity',
    code: 'VRP-SR',
    tagline: 'Vibration response profiling',
    accent: '#7fe8c9',
    runsOn: 'on-device + server',
    model: 'Goertzel fingerprint → 4-head one-class anomaly ensemble (IF, PCA, Mahalanobis, cross-modal)',
    modelCard: [
      '60s sweep 30→110 Hz; dual sensing: 240fps rear camera + linear accel at ~416 Hz.',
      'Goertzel narrowband filter per target frequency → float[2K] fingerprint (optical + inertial dB).',
      'Backend ensemble: IsolationForest + PCA recon error + Ledoit-Wolf Mahalanobis + cross-modal coupling head.',
      'Cross-modal head (novel): fires on optical↔inertial coupling integrity, not feature anomaly.',
      'Split-conformal calibrated thresholds. Legacy FPR 40% → ML FPR 3.1%.',
    ],
    crypto: 'Fingerprint signed on-device; server scores via iforest.onnx + pca.onnx + head_meta.json.',
    liveMetrics: ['resonance_hz', 'q_factor', 'ensemble_score', 'sweep_hz'],
  },
  {
    kind: 'magneto',
    index: 7,
    name: 'Component Authenticity',
    code: 'MSA',
    tagline: 'Magnetic-signature authenticity',
    accent: '#a8c5f0',
    runsOn: 'on-device + server',
    model: 'Hard-iron offset preservation + diagonal-covariance Mahalanobis (χ² confidence)',
    modelCard: [
      'Inverts standard magnetometer calibration: keeps hard-iron offset h as the authenticity signature.',
      'Linearised sphere fit (4×4 normal equations, closed-form Cholesky, ridge=1e-6) over 60s figure-8 motion.',
      'Verdict requires BOTH Mahalanobis ≥ 3.5 AND |magZ| ≥ 3.5 (Bonferroni-style AND).',
      'Confidence: closed-form χ²(3) survival. Quality gate: JSON-of-trees GBM walked on-device.',
    ],
    crypto: 'MSA report signed with fresh TEE key; server recomputeVerdict() vs MagnetoModelBaseline.',
    liveMetrics: ['mahalanobis', 'mag_z', 'earth_magnitude_ut', 'coverage'],
  },
  {
    kind: 'selfemit',
    index: 8,
    name: 'Multi-Sensor Self-Emissions',
    code: 'MSE',
    tagline: 'Component self-emission drift',
    accent: '#c9a8f0',
    runsOn: 'on-device + server',
    model: 'GLCT chirplet transform → SupCon-trained ONNX heads + anchor-drift detector',
    modelCard: [
      '7 activations (backlight, refresh, vibration chirp, audio sweep, AF, NFC, charging) with 5 concurrent capture streams.',
      'General Linear Chirplet Transform (Yu & Zhou 2016) → per-component 128-d embeddings (INT8 ONNX heads).',
      'Anchor-drift detector: drift = max(d_cos/τ_cos, d_mah/τ_mah); device is its own reference.',
      'Channels missing self-distance targets demoted to EXPLORATORY (w=0.1) — disciplined honesty.',
    ],
    crypto: 'Canonical JSON (sorted keys, %.6e floats) → SHA-256 challenge → detached ECDSA; CBOR log.',
    liveMetrics: ['vibration_drift', 'speaker_drift', 'battery_drift', 'composite'],
  },
  {
    kind: 'ultrasonic',
    index: 9,
    name: 'Acoustic Self-Inspection',
    code: 'AST',
    tagline: 'Ultrasonic seal-integrity (flagship)',
    accent: '#f0a35e',
    runsOn: 'on-device + server',
    model: 'Farina sweep + Empirical-CDF / Stouffer detector (+ server ONNX IsolationForest)',
    modelCard: [
      '48 kHz Tukey-windowed 18→23.5 kHz exponential (Farina) sweep at −3 dBFS, UNPROCESSED capture.',
      'Farina deconvolution → Hilbert envelope → Schroeder backward integration of squared IR.',
      'ECDF/Stouffer anomaly: two-sided ECDF tail p per feature, combined via summed −log p.',
      'Calibrated by leave-one-campaign-out CV. Within-session damaged-vs-control AUC = 1.000.',
      'Server: Gaussian sum-of-squared-z + ONNX IsolationForest; combined verdict.',
    ],
    crypto: 'Feature-vector hash bound into attestation challenge; signed on-device.',
    liveMetrics: ['in_band_snr_db', 't60_s', 'near_peak_leakage_db', 'anomaly_score'],
  },
  {
    kind: 'drop',
    index: 10,
    name: 'Drop Forensics',
    code: 'DROP',
    tagline: 'Impact event reconstruction',
    accent: '#f0726c',
    runsOn: 'on-device',
    model: 'EvenT-ML FSM + multi-task 1-D CNN (ORT) with per-event Merkle leaves',
    modelCard: [
      'Always-armed significant-motion trigger → 500 Hz accel+gyro 2s window (1s pre + 1s post).',
      'Detection FSM: IDLE → MAYBE_FALL (|a|/g<0.5) → IN_FALL (100ms) → IMPACT (|a|/g>1.4).',
      'Physics: kinetic-energy drop-height h=0.5·g·t²; restitution proxy = rebound/approach.',
      'Multi-task CNN: head_drop_binary, head_surface_5, head_corner_6, head_height_regression (~40ms/window).',
      'Honest: peak_g_saturated flag when within 1% of ±8g HAL cap.',
    ],
    crypto: 'Per-event fresh EC-P256 key; signs the Merkle leaf (not the event); linear append-only DropMerkleLog.',
    liveMetrics: ['peak_g', 'free_fall_ms', 'drop_height_cm', 'restitution'],
  },
  {
    kind: 'acta',
    index: 11,
    name: 'Autonomous Self-Audit',
    code: 'ACTA',
    tagline: 'The phone that audited itself',
    accent: '#7fe8c9',
    runsOn: 'on-device + server',
    model: 'Autonomous-agent coverage attestation; TEE-signed per-action RFC-6962 Merkle log',
    modelCard: [
      'Foreground agent: observe → plan → execute → sign → log, strictly sequential.',
      '22 subsystem probes per cycle (WiFi/BT/NFC/cellular/GPS/storage/battery/CPU/sensors/touch grid/S-Pen...).',
      'Evidence enriched into paramsCanonicalSha256 so the TEE signature attests the actual measurement.',
      'Coverage finalize(): UNTESTED → NOT_EXERCISED. Refuses to mark unverified probes as PASSED.',
      '138-byte fixed binary leaf body; server verifyActaBundle() checks Merkle root + nonce + leaf count.',
    ],
    crypto: 'Single TEE EC P-256 key per session; signFinal = ECDSA over SHA-256(root‖nonce‖manifestHash).',
    liveMetrics: ['leaves', 'coverage_fraction', 'passed', 'failed', 'untested'],
  },
  {
    kind: 'thermal',
    index: 12,
    name: 'Thermal Provenance',
    code: 'TPROV',
    tagline: 'Stress-response provenance (3-state)',
    accent: '#f0726c',
    runsOn: 'on-device + server',
    model: 'Vulkan compute stress + Foster RC lumped thermal model, three-state verdict',
    modelCard: [
      '330s / 7-phase trajectory: cooldown, CPU, cool, GPU (Vulkan Mandelbrot), cool, mixed, trail-cool.',
      'GPU stress: dlopen libvulkan.so → Mandelbrot SPIR-V compute, 1024×1024 grid, GPU-timeline nanos.',
      'Ambient compensation: Foster RC lumped model T_skin = T_amb + P·R_th·(1−exp(−t/τ)).',
      'Verdict: WITHIN_ENVELOPE / FLAG_FOR_INSPECTION / OUTSIDE_ENVELOPE (requires SEVERE thermal event).',
      'Refuses binary AGED/HEALTHY on principle — three-state only.',
    ],
    crypto: 'CBOR bundle (deterministic) → SHA-256 challenge (tag 0x0D); MerkleAppender leaf hash.',
    liveMetrics: ['skin_temp_c', 'headroom', 'gpu_dispatches', 'tau_s'],
  },
  {
    kind: 'wipe',
    index: 13,
    name: 'Forensic Wipe',
    code: 'WIPE',
    tagline: 'Proof-of-Space-Time erasure',
    accent: '#e8b582',
    runsOn: 'on-device + server',
    model: 'K1/K2 TEE attestation + ChaCha20 Proof-of-Space-Time + RFC-6962 Merkle',
    modelCard: [
      'K1 attest pre-reset → factory reset → K2 reclaim post-reset (absence of K1 = "fresh device" proof).',
      'Proof of Space-Time: ChaCha20 PRG writes 4 KiB blocks, fsynced, streamed into RFC-6962 Merkle.',
      'Verifier-driven challenge: device re-derives keystream byte-for-byte (StorageDriftException on mismatch).',
      'Evidence Packet (COSE_Sign1) → Sigstore Rekor → OpenTimestamps → Bitcoin.',
      'Optional in the live demo (destructive). The composite certificate does not require a wipe.',
    ],
    crypto: 'K1/K2 hardware-attested EC P-256 keys; PoST Merkle root; Rekor + OTS anchoring.',
    liveMetrics: ['committed_mb', 'challenges', 'merkle_root'],
  },
];

export const PILLAR_BY_KIND: Record<string, PillarMeta> = Object.fromEntries(
  PILLARS.map((p) => [p.kind, p]),
);
